import { Link } from "react-router-dom";
import { useUiLang } from "../../../stores/uiLang";

function SopipaAcknowledgmentPageEn() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">California SOPIPA Acknowledgment</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Student Online Personal Information Protection Act compliance
          statement for California school and district customers.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="text-xs text-gray-500">
          <strong>Statute:</strong> Cal. Bus. &amp; Prof. Code §§ 22584–22585 &nbsp;|&nbsp;
          <strong>Operator:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Effective Date:</strong> May 27, 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Scope</h2>
          <p className="mb-3">
            Project Explore is an Internet-based educational platform designed
            and marketed exclusively for use by K-12 schools and districts.
            Startup Smartup is an "operator" within the meaning of Cal. Bus.
            &amp; Prof. Code § 22584(a)(3).
          </p>
          <p className="mb-3">
            This acknowledgment applies to all California school or district
            customers of Project Explore and to any student who accesses the
            platform through a California school or district.
          </p>
          <p>
            Provider currently publishes acknowledgment of compliance with
            California's SOPIPA. Provider will publish state-specific
            acknowledgments for Illinois (SOPPA), New York (Education Law §
            2-d), Connecticut (PA 16-189), and other state student data privacy
            laws as district demand warrants. Provider's underlying practices
            already meet the substantive requirements of these analogous
            statutes; the published acknowledgments document specific statutory
            references.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. Prohibited Practices — What Project Explore Does NOT Do
          </h2>
          <p className="mb-3">
            In compliance with § 22584(b)(1), Startup Smartup does not:
          </p>
          <p className="mb-2">
            <strong>(a) Targeted advertising.</strong> Project Explore does not
            engage in targeted advertising to students or their parents or legal
            guardians when the targeting is based on covered information
            obtained from student use of the platform.
          </p>
          <p className="mb-2">
            <strong>(b) Student profiling.</strong> Project Explore does not use
            covered information to amass a profile about a K-12 student for any
            purpose other than K-12 school purposes.
          </p>
          <p className="mb-2">
            <strong>(c) Sale or rental of covered information.</strong> Project
            Explore does not sell, rent, trade, or otherwise exchange any
            student covered information for any consideration, including but not
            limited to monetary payment, in-kind services, analytics access,
            marketing access, or any other form of value transfer.
          </p>
          <p>
            <strong>(d) Unauthorized disclosure.</strong> Project Explore does
            not disclose covered information unless one of the following
            exceptions applies:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
            <li>To carry out K-12 school purposes, if the recipient is prohibited from using the covered information for any purpose other than providing the contracted service</li>
            <li>As required by state or federal law or by a court order</li>
            <li>For a legitimate research purpose under certain conditions, with student identity information removed</li>
            <li>As specifically directed by the school or district, or with the consent of the parent or student (as applicable)</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Affirmative Obligations — What Project Explore Does
          </h2>
          <p className="mb-3">
            In compliance with § 22584(b)(2)–(b)(4) and § 22584(d):
          </p>
          <p className="mb-2">
            <strong>(a) Reasonable security.</strong> Startup Smartup implements
            and maintains reasonable security procedures and practices
            appropriate to the nature of the covered information, consistent
            with the obligation at § 22584(d). See the{" "}
            <Link to="/legal/incident-response" className="text-indigo-600 underline">
              Incident Response Policy
            </Link>{" "}
            for a summary of those practices.
          </p>
          <p className="mb-2">
            <strong>(b) Deletion on request.</strong> Upon request by a school
            or district, Startup Smartup will delete a student's covered
            information from the platform within 30 days of the request, unless
            retention is required by applicable law. (§ 22584(d)(2).)
          </p>
          <p>
            <strong>(c) Disclosure only as permitted.</strong> Startup Smartup
            discloses covered information only as permitted under § 22584(e)
            (see Section 2(d) above).
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">4. Covered Information</h2>
          <p className="mb-3">"Covered information" under SOPIPA includes:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Student first name and last name</li>
            <li>Student grade level</li>
            <li>Student email address (collected for students in grade 3 and above where provided by the district)</li>
            <li>Assessment responses, scores, and related data</li>
            <li>Video viewing analytics and progress data</li>
            <li>AI-generated feedback tied to a student's assignment submissions</li>
            <li>Student IEP/504 accommodation flags (where applicable)</li>
            <li>Parent/guardian contact information (where provided by the district)</li>
          </ul>
          <p>
            Startup Smartup does not collect social security numbers, home
            addresses, biometric data, financial information, or health
            information outside of IEP/504 accommodation flags provided by the
            district.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Consistency with Other Compliance Statements
          </h2>
          <p className="mb-2">
            This acknowledgment is consistent with and supplements:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Link to="/privacy#coppa" className="text-indigo-600 underline">COPPA Addendum</Link></li>
            <li><Link to="/ferpa" className="text-indigo-600 underline">FERPA Statement</Link></li>
            <li><Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link></li>
            <li><Link to="/legal/dpa" className="text-indigo-600 underline">Data Processing Agreement (SDPC NDPA)</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">6. Contact</h2>
          <p className="mb-2">
            Questions about this acknowledgment or SOPIPA compliance should be
            directed to:
          </p>
          <p>
            <strong>Justin Ghiglia, Privacy Contact</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Effective Date: May 27, 2026
        </div>
      </section>
    </div>
  );
}

function SopipaAcknowledgmentPageEs() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Reconocimiento de SOPIPA de California</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Declaración de cumplimiento de la Ley de Protección de la Información
          Personal de los Estudiantes en Línea (SOPIPA) para escuelas y
          distritos clientes de California.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
          Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.
        </p>
        <p className="text-xs text-gray-500">
          <strong>Ley:</strong> Código de Negocios y Profesiones de California, §§ 22584 y 22585 &nbsp;|&nbsp;
          <strong>Operador:</strong> Startup Smartup, 2380 California Street #402, San Francisco, CA 94115 &nbsp;|&nbsp;
          <strong>Fecha de entrada en vigor:</strong> 27 de mayo de 2026
        </p>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">1. Alcance</h2>
          <p className="mb-3">
            Project Explore es una plataforma educativa basada en internet,
            diseñada y comercializada exclusivamente para escuelas y distritos
            de K-12. Startup Smartup es un "operador" en el sentido del Código
            de Negocios y Profesiones de California, § 22584(a)(3).
          </p>
          <p className="mb-3">
            Este reconocimiento se aplica a todas las escuelas o distritos
            clientes de Project Explore en California y a todo estudiante que
            entre a la plataforma por medio de una escuela o un distrito de
            California.
          </p>
          <p>
            Actualmente el Proveedor publica un reconocimiento de cumplimiento
            de la SOPIPA de California. El Proveedor publicará reconocimientos
            específicos para Illinois (SOPPA), Nueva York (Education Law § 2-d),
            Connecticut (PA 16-189) y otras leyes estatales de privacidad de
            datos de estudiantes, según lo pida la demanda de los distritos.
            Las prácticas del Proveedor ya cumplen los requisitos de fondo de
            estas leyes análogas; los reconocimientos publicados documentan las
            referencias legales específicas.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. Prácticas prohibidas: lo que Project Explore NO hace
          </h2>
          <p className="mb-3">
            En cumplimiento del § 22584(b)(1), Startup Smartup no:
          </p>
          <p className="mb-2">
            <strong>(a) Publicidad dirigida.</strong> Project Explore no hace
            publicidad dirigida a los estudiantes ni a sus padres o tutores
            legales cuando la segmentación se basa en información cubierta
            obtenida del uso de la plataforma por parte de los estudiantes.
          </p>
          <p className="mb-2">
            <strong>(b) Perfiles de estudiantes.</strong> Project Explore no usa
            información cubierta para armar un perfil de un estudiante de K-12
            con un fin distinto de los fines escolares de K-12.
          </p>
          <p className="mb-2">
            <strong>(c) Venta o alquiler de información cubierta.</strong>{" "}
            Project Explore no vende, alquila, intercambia ni de ninguna otra
            forma da información cubierta de los estudiantes a cambio de
            ninguna contraprestación, incluidos, entre otros, pagos en dinero,
            servicios en especie, acceso a análisis, acceso a mercadotecnia o
            cualquier otra forma de transferencia de valor.
          </p>
          <p>
            <strong>(d) Divulgación no autorizada.</strong> Project Explore no
            divulga información cubierta, salvo que se aplique una de las
            siguientes excepciones:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
            <li>Para cumplir fines escolares de K-12, si al destinatario se le prohíbe usar la información cubierta para cualquier fin distinto de prestar el servicio contratado</li>
            <li>Cuando lo exija la ley estatal o federal o una orden judicial</li>
            <li>Para un fin legítimo de investigación, bajo ciertas condiciones, sin la información que identifica al estudiante</li>
            <li>Según lo indique específicamente la escuela o el distrito, o con el consentimiento del padre o del estudiante (según corresponda)</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Obligaciones afirmativas: lo que Project Explore SÍ hace
          </h2>
          <p className="mb-3">
            En cumplimiento del § 22584(b)(2) a (b)(4) y del § 22584(d):
          </p>
          <p className="mb-2">
            <strong>(a) Seguridad razonable.</strong> Startup Smartup aplica y
            mantiene procedimientos y prácticas de seguridad razonables,
            adecuados a la naturaleza de la información cubierta, de acuerdo
            con la obligación del § 22584(d). Consulta la{" "}
            <Link to="/legal/incident-response" className="text-indigo-600 underline">
              Política de respuesta a incidentes
            </Link>{" "}
            para ver un resumen de esas prácticas.
          </p>
          <p className="mb-2">
            <strong>(b) Eliminación a solicitud.</strong> Cuando una escuela o
            un distrito lo pida, Startup Smartup eliminará de la plataforma la
            información cubierta de un estudiante en un plazo de 30 días después
            de la solicitud, salvo que la ley aplicable exija conservarla.
            (§ 22584(d)(2).)
          </p>
          <p>
            <strong>(c) Divulgación solo como se permite.</strong> Startup
            Smartup divulga información cubierta solo como lo permite el
            § 22584(e) (consulta la Sección 2(d) más arriba).
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">4. Información cubierta</h2>
          <p className="mb-3">La "información cubierta" bajo SOPIPA incluye:</p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Nombre y apellido del estudiante</li>
            <li>Grado escolar del estudiante</li>
            <li>Correo electrónico del estudiante (se recopila de estudiantes de 3.er grado en adelante, cuando lo proporciona el distrito)</li>
            <li>Respuestas de evaluaciones, calificaciones y datos relacionados</li>
            <li>Estadísticas de reproducción de video y datos de progreso</li>
            <li>Comentarios generados por IA vinculados a los trabajos entregados por un estudiante</li>
            <li>Indicadores de adaptaciones IEP/504 del estudiante (cuando corresponda)</li>
            <li>Información de contacto del padre, madre o tutor (cuando la proporciona el distrito)</li>
          </ul>
          <p>
            Startup Smartup no recopila números de Seguro Social, domicilios,
            datos biométricos, información financiera ni información de salud,
            aparte de los indicadores de adaptaciones IEP/504 que proporciona
            el distrito.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Coherencia con otras declaraciones de cumplimiento
          </h2>
          <p className="mb-2">
            Este reconocimiento es coherente con lo siguiente y lo complementa:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><Link to="/privacy#coppa" className="text-indigo-600 underline">Anexo de COPPA</Link></li>
            <li><Link to="/ferpa" className="text-indigo-600 underline">Declaración de FERPA</Link></li>
            <li><Link to="/privacy" className="text-indigo-600 underline">Política de privacidad</Link></li>
            <li><Link to="/legal/dpa" className="text-indigo-600 underline">Acuerdo de procesamiento de datos (SDPC NDPA)</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">6. Contacto</h2>
          <p className="mb-2">
            Las preguntas sobre este reconocimiento o sobre el cumplimiento de
            SOPIPA deben dirigirse a:
          </p>
          <p>
            <strong>Justin Ghiglia, contacto de privacidad</strong>
            <br />
            Startup Smartup
            <br />
            2380 California Street #402, San Francisco, CA 94115
            <br />
            <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Fecha de entrada en vigor: 27 de mayo de 2026
        </div>
      </section>
    </div>
  );
}

export default function SopipaAcknowledgmentPage() {
  return useUiLang() === "es" ? <SopipaAcknowledgmentPageEs /> : <SopipaAcknowledgmentPageEn />;
}
